import { isNumber } from 'lodash';
import numeral from 'numeral';

import { getIntlLocale } from './utilities';
// ----------------------------------------------------------------------

const getDecimalPlaces = (num: number) => {
  const absNum = Math.abs(num);
  if (absNum < 1000) return 0;
  if (absNum >= 1000 && absNum < 10000) return 2;
  return 1;
};

export const fReadableCurrency = (
  number: number | undefined | null,
  currencyISO?: string,
  locale = 'en-GB',
) => {
  if (!isNumber(number)) return 'N/A';

  const decimals = getDecimalPlaces(number);

  const intlLocale = getIntlLocale(locale);
  const currencyFormatted = new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    notation: 'compact',
    currency: currencyISO || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return `${currencyFormatted.format(number)}`;
};

export function fCurrency(
  number: number | undefined | null,
  currencyISO?: string | undefined,
  fractionalDigits?: number,
) {
  if (number == null) {
    return 'N/A';
  }

  const currencyFormatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyISO || 'EUR',
    minimumFractionDigits: fractionalDigits || 0,
    maximumFractionDigits: fractionalDigits || 0,
  });

  return currencyFormatted.format(parseInt(number.toString(), 10));
}

// TODO: remove this function when old code has been removed
export function fCurrencyThousand(
  number: number | undefined | null,
  currencyISO: string | undefined,
) {
  const fractionalDigits =
    number != null && isNumber(number) && Math.abs(number) < 10000 ? 1 : 0;

  const currencyFormatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyISO || 'EUR',
    minimumFractionDigits: fractionalDigits,
    maximumFractionDigits: fractionalDigits,
  });

  return isNumber(number)
    ? `${currencyFormatted.format(number / 1000)} K`
    : 'N/A';
}

export function fPercent(number: number) {
  return numeral(number).format('0,0%');
}

export function fPercentWithThreshold(
  number: number,
  threshold: { value: number; label: string },
) {
  if (number < threshold.value) {
    return threshold.label;
  }

  return fPercent(number);
}

export function fPercentTwoDp(number: number) {
  return numeral(number).format('0.00%');
}

/**
 * Formats a number to a string with the intl number format.
 */
export function fNumber(
  number?: number | null,
  /** @deprecated Use the `locale` argument for Intl-based formatting instead. */
  format?: string,
  locale?: string,
) {
  if (number == null) {
    return 'N/A';
  }

  // Use Intl.NumberFormat for locale-aware formatting when locale is provided
  if (locale && !format) {
    try {
      const intlLocale = getIntlLocale(locale);
      return new Intl.NumberFormat(intlLocale, {
        maximumFractionDigits: 1,
      }).format(number);
    } catch {
      // Fallback to numeral if locale is invalid
    }
  }

  return numeral(number).format(format);
}

// TODO: we should be using this kind of cache for all Intl.NumberFormat instances
// since creating a new instance is expensive, especially given that we do this multiple times
// for every table row in a data grid
const cache = new Map<string, Intl.NumberFormat>();
export function fShortenNumber(
  number: number,
  locale: string,
  options?: Omit<Intl.NumberFormatOptions, 'notation' | 'compactDisplay'>,
) {
  const intlLocale = getIntlLocale(locale);
  const key = intlLocale + '|' + JSON.stringify(options);
  let formatter = cache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
      ...(options ?? {}),
    });
    cache.set(key, formatter);
  }

  return formatter.format(number);
}

export function fData(number: number) {
  return numeral(number).format('0.0 b');
}

export function fNumberWithCommas(number?: number | null, locale?: string) {
  if (number == null) {
    return 'N/A';
  }

  // Use Intl.NumberFormat for proper locale-aware formatting
  if (locale) {
    try {
      return new Intl.NumberFormat(locale).format(number);
    } catch {
      // Fallback to manual comma insertion if locale is invalid
    }
  }

  // Fallback: manual comma insertion (English style)
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function fRoundedNumberWithCommas(number?: number | null) {
  if (number == null) {
    return 'N/A';
  }

  return Math.round(number)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function fPercentage(number: number) {
  return numeral(number).format('0%');
}

export function fNumberNull(number?: number | null) {
  if (number == null || isNaN(number)) {
    return 'N/A';
  }

  return number;
}

export function fNullData(data: null): 'N/A';

export function fNullData(data: '-'): 'N/A';

export function fNullData<Data>(data: Data): Data;
export function fNullData<Data>(data: Data | null | '-'): Data | 'N/A' {
  return data == null || data === '-' ? 'N/A' : (data as Data);
}

export function fRound(number: number, decimalPlaces: number = 0) {
  const format = `0.[${'0'.repeat(decimalPlaces)}]`;
  return numeral(number).format(format);
}
